import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import HelpNav from './HelpNav.jsx';
import HelpContent from './HelpContent.jsx';
import { HELP_SECTIONS, getHelpLocation } from './helpContent.js';
import './help.scss';

/**
 * Slide-in help drawer: backdrop, Escape-to-close, and body-scroll lock while
 * open. Opens on the section + topic that matches the current route. Rendered
 * by the shared layout and toggled from the `?` button in the top bar.
 */
export default function HelpDrawer({ open, onClose }) {
  const location = useLocation();
  const [sectionId, setSectionId] = useState('welcome');
  const [topicId, setTopicId] = useState('overview');

  // On open, jump to the topic most relevant to the current page.
  useEffect(() => {
    if (open) {
      const loc = getHelpLocation(location.pathname);
      setSectionId(loc.sectionId);
      setTopicId(loc.topicId);
    }
  }, [open, location.pathname]);

  // Escape closes; lock body scroll while open.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  const activeSection = HELP_SECTIONS.find((s) => s.id === sectionId) || HELP_SECTIONS[0];

  const handleSection = (id) => {
    setSectionId(id);
    const section = HELP_SECTIONS.find((s) => s.id === id);
    if (section) setTopicId(section.topics[0].id);
  };

  return (
    <>
      <div className="help-backdrop" onClick={onClose} aria-hidden="true" />
      <aside className="help-drawer" role="dialog" aria-modal="true" aria-label="Help">
        <div className="help-header">
          <span className="fw-bold">
            <i className="fa-solid fa-circle-question me-2" aria-hidden="true" />
            BunkerOS Help
          </span>
          <button type="button" className="help-close" onClick={onClose} aria-label="Close help">
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
        </div>

        <HelpNav sections={HELP_SECTIONS} activeSectionId={sectionId} onSelect={handleSection} />
        <HelpContent section={activeSection} activeTopicId={topicId} onSelectTopic={setTopicId} />
      </aside>
    </>
  );
}
